import React, { useEffect, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress as MUIProgress,
} from "@mui/material";

import { FUNDS_API_URLS, FundsAPI } from "../../../../api/funds";
import { ACCOUNTS_API_URLS } from "../../../../api/accounts";
import { EVALUATIONS_API_URLS, EvaluationsAPI } from "../../../../api/evaluations";
import { AGENT_ACTIVITY_LOG_API_URLS } from "../../../../api/agentActivityLog";
import { CircularProgress, Snackbar } from "../../../../components/ui";
import stateReducer, { initialState } from "./stateReducer";
import DialogActionsButtons from "./components/DialogActionsButtons";
import DialogConfirmFundedOrBannedData from "./components/DialogConfirmFundedOrBannedData";
import { useAdminProviderDataContext, useCustomerProvider } from "../../../../context";
import { customerActions } from "../../../../reducers/customer";
import SimilarFundedAccountsData from "./components/SimilarFundedAccountsData";

const DTitle = () => <DialogTitle>Update subscription status</DialogTitle>;
const DialogContentQuestion = () => (
  <DialogContentText>
    Would you like to update this subscription status to <b>passed</b>?
  </DialogContentText>
);

const CompleteEvaluation = (): React.ReactElement => {
  const params: { id: string } = useParams();
  const id = +params.id;
  const rDispatch = useDispatch();
  const queryClient = useQueryClient();
  const [assignToFundedTrader, setAssignToFundedTrader] = useState<null | number>(null);
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const {
    user: { allowed_features },
  } = useAdminProviderDataContext();
  const { customer, getCustomer } = useCustomerProvider();

  const {
    plans: {
      data: { activePlan },
    },
    mainData: {
      loading: isLoadingMainData,
      data: { funded_trader, banned_account },
    },
    managementData: {
      data: { status: account_status },
    },
  } = useSelector((state) => state.customer);
  const { plans } = useSelector((state) => state.plans.data);

  const isAvailablePlanForPassing =
    plans.findIndex(
      (plan) => plan.id === activePlan?.plans_id && plan.is_available_for_evaluation_complete
    ) !== -1;
  const isAllowedToCompleteEvaluation =
    allowed_features.complete_evaluation || allowed_features.access_all_features;
  const isInactiveAccount = account_status === 0;

  const handleCancel = () => dispatch({ type: "closeDialog" });
  const handleClickOpen = async () => {
    await getCustomer();
    dispatch({ type: "openDialog" });
  };

  const handleCompleteEvaluation = useMutation({
    mutationKey: [EVALUATIONS_API_URLS.evaluationComplete, id],
    mutationFn: async () => {
      return await EvaluationsAPI.evaluationCompleteRequest(id.toString(), {
        intercom_id: customer.intercom?.id as string,
        e2t_account_id: customer.intercom?.custom_attributes?.["Gauntlet Account Name"],
        email: customer.email,
        type_and_size: activePlan?.plan.key as string,
        assignToFundedTrader,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_API_URLS.accountPlans],
      });
      queryClient.invalidateQueries({
        queryKey: [AGENT_ACTIVITY_LOG_API_URLS.getCustomerLogs, id],
      });
      dispatch({
        type: "onSuccessResponse",
        payload: { responseSuccessMessage: "Evaluation completed successfully" },
      });
    },
    onError: (error: {
      response: {
        data: {
          intercom: {
            statusText: string;
          };
        };
      };
    }) => {
      const isIntercomError = error?.response?.data?.intercom;
      const intercomErrorText = "Error completing evaluation. Intercom error:";

      dispatch({
        type: "onErrorResponse",
        payload: {
          responseErrorMessage: isIntercomError
            ? `${intercomErrorText} ${error.response.data.intercom.statusText}`
            : "Error completing evaluation",
        },
      });
    },
    onSettled: () => {
      setTimeout(() => {
        dispatch({ type: "onSettled" });
      }, 1500);
    },
  });

  const handleConfirm = () => {
    if ((funded_trader?.ban_date || banned_account?.timestamp) && !state.openConfirmation) {
      dispatch({ type: "openConfirmationForBannedAccounts" });
    } else {
      handleCompleteEvaluation.mutate();
    }
  };

  const handleCloseSnackbar = (_?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") return;
    dispatch({ type: "onSettled" });
  };

  const isDisabledActionButton = isLoadingMainData || handleCompleteEvaluation.isPending;
  const isDisabledEvaluationCompletedButton =
    !isAllowedToCompleteEvaluation ||
    !isAvailablePlanForPassing ||
    isInactiveAccount ||
    isLoadingMainData;

  const { isError, error } = useQuery({
    enabled: state.open,
    queryKey: [FUNDS_API_URLS.findSimilarFundedAccounts, id],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("accounts_id", id.toString());
      query.append(
        "e2t_account_id",
        customer.intercom?.custom_attributes?.["Gauntlet Account Name"] as string
      );
      query.append("email", customer.email);
      query.append("name", customer.name);
      query.append("phone", customer.phone);
      query.append("address", customer.address);
      rDispatch(customerActions.fetchSimilarFundedAccountsRequest());
      const response = await FundsAPI.findSimilarFundedAccountsRequest(query.toString(), signal);
      rDispatch(customerActions.fetchSimilarFundedAccountsSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching similar funded accounts", error);
      rDispatch(customerActions.fetchSimilarFundedAccountsFailed());
    }
  }, [isError, error]);

  return (
    <div>
      <Dialog open={state.open} onClose={handleCancel}>
        <Dialog open={state.openConfirmation}>
          <DTitle />

          <DialogContent dividers>
            <DialogContentQuestion />

            <DialogConfirmFundedOrBannedData
              funded_trader_ban_date={funded_trader?.ban_date}
              banned_account_timestamp={banned_account?.timestamp}
            />

            <DialogContentText>
              <b>Please consult with management and compliance before proceeding.</b>
            </DialogContentText>
          </DialogContent>

          <DialogActionsButtons
            handleCancel={handleCancel}
            handleConfirm={handleConfirm}
            isDisabledActionButton={isDisabledActionButton}
          />
        </Dialog>

        <DTitle />
        <DialogContent dividers>
          <CircularProgress isLoading={isLoadingMainData} />
          <DialogContentText>
            {funded_trader && (
              <b>
                Trader already funded with status:{" "}
                {funded_trader?.funded_trader_status?.status_name}
              </b>
            )}
          </DialogContentText>
          <SimilarFundedAccountsData
            assignToFundedTrader={assignToFundedTrader}
            setAssignToFundedTrader={setAssignToFundedTrader}
          />
          <DialogContentQuestion />
        </DialogContent>
        <DialogActionsButtons
          handleCancel={handleCancel}
          handleConfirm={handleConfirm}
          isDisabledActionButton={isDisabledActionButton}
        />
      </Dialog>

      <Button
        sx={{ textTransform: "none" }}
        size="small"
        onClick={handleClickOpen}
        disabled={isDisabledEvaluationCompletedButton}
        endIcon={isLoadingMainData && <MUIProgress size={20} />}
      >
        Evaluation Completed
      </Button>

      <Snackbar
        type={state.responseSuccessMessage ? "success" : "error"}
        isOpenSnackbar={state.openSnackbar}
        responseSuccessMessage={state.responseSuccessMessage as string}
        responseErrorMessage={state.responseErrorMessage as string}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default CompleteEvaluation;
